import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuItem } from '@/mocks/menu';

interface MenuCardProps {
  item: MenuItem;
  viewMode: 'grid' | 'list';
  onOpenDetail: (item: MenuItem) => void;
}

export default function MenuCard({ item, viewMode, onOpenDetail }: MenuCardProps) {
  const { t, i18n } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const isList = viewMode === 'list';
  const isRTL = i18n.language === 'ar';

  const priceLabel = isRTL
    ? `${item.price} ${t('menu.currency')}`
    : `${t('menu.currency')} ${item.price}`;

  return (
    <div
      className={`
        group relative bg-background-50 rounded-xl overflow-hidden border border-background-200/70
        hover:border-primary-300/60 hover:ring-1 hover:ring-primary-300/30
        transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-primary-500/5
        active:scale-[0.99] active:duration-75
        h-full
        ${isList ? 'flex flex-row' : 'flex flex-col'}
      `}
    >
      {/* Image Container - clickable */}
      <div
        className={`
          relative overflow-hidden bg-background-100 flex-shrink-0 cursor-pointer
          ${isList ? 'w-28 sm:w-36 md:w-44 aspect-square' : 'w-full aspect-square'}
        `}
        onClick={() => onOpenDetail(item)}
        role="button"
        tabIndex={0}
        aria-label={`${t(`item.${item.id}.name`)} - ${t('menu.detail')}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenDetail(item);
          }
        }}
      >
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary-300 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {!imageError && (
          <img
            src={item.image}
            alt={t(`item.${item.id}.name`)}
            className={`w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        )}
        {imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-background-100">
            <span className="w-12 h-12 flex items-center justify-center text-secondary-400">
              <i className="ri-image-line text-2xl" />
            </span>
          </div>
        )}

        {/* Popular badge */}
        {item.popular && (
          <div className="absolute top-2 start-2 md:top-3 md:start-3 z-10">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-accent-500 text-background-50 text-[10px] md:text-xs font-semibold rounded-full shadow-sm">
              <i className="ri-fire-line text-[10px] md:text-xs" />
              {t('menu.popular')}
            </span>
          </div>
        )}
      </div>

      {/* Content - clickable */}
      <div
        className={`p-3 md:p-4 cursor-pointer ${isList ? 'flex-1 flex flex-col justify-center min-w-0' : 'flex-1 flex flex-col'}`}
        onClick={() => onOpenDetail(item)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenDetail(item);
          }
        }}
      >
        <div className={`flex items-start justify-between gap-2 mb-1 ${isList ? 'md:mb-2' : ''}`}>
          <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 leading-snug truncate transition-colors duration-300 group-hover:text-primary-600">
            {t(`item.${item.id}.name`)}
          </h3>
          <span className="text-primary-600 font-bold text-sm md:text-base whitespace-nowrap flex-shrink-0">
            {priceLabel}
          </span>
        </div>
        <p className={`text-foreground-500 text-xs md:text-sm leading-relaxed ${isList ? 'line-clamp-2 md:line-clamp-3' : 'line-clamp-2'}`}>
          {t(`item.${item.id}.desc`)}
        </p>
      </div>
    </div>
  );
}